import { FC, useCallback } from 'react';
import { AdvancedFilterRow } from 'shared/components/AdvancedFilter/components/AdvancedFilterRow';
import { InlineFilterGroup } from 'shared/components/InlineFilterGroup';
import { useSelector } from 'react-redux';
import { selectUndergroundTimeFilter } from 'shared/selectors/advancedFilters';
import { selectIsSuburbanListing } from 'shared/selectors/jsonQuery';
import { InlineFilter } from 'shared/components/InlineFilter';
import { MaskedInput } from 'shared/components/MaskedInput';
import { useDebouncedCallback, useFilterChangeCallback } from 'shared/hooks';
import { EMetroTravelType } from '../../../../../../../packages/api-models/common/json_query';
import { TModifier } from 'shared/types/filters';
import { DEFAULT_TRAVEL_TIME } from './constants';
import { SingleSelectionAdvancedFilter } from 'shared/components/AdvancedFilter';

export const UndergroundTimeContainer: FC = () => {
  const {
    label,
    traveTimeFilter: { label: prefix, value: travelTime, postfix },
    travelTypeFilter: { value: travelType, options },
  } = useSelector(selectUndergroundTimeFilter);
  const isSuburbanListing = useSelector(selectIsSuburbanListing);

  const onChange = useFilterChangeCallback();

  const handleTravelTimeChange = useDebouncedCallback(
    (time: string) => {
      const nextTime = time ? Number(time) : null;
      const [currentTravelType] = travelType;

      const modifiers: TModifier[] = [];

      if (nextTime && !currentTravelType) {
        const nextTravelType = isSuburbanListing ? EMetroTravelType.MapTransport : EMetroTravelType.MapFoot;

        modifiers.push(
          { action: 'setUndergroundTravelTime', arguments: [nextTime] },
          { action: 'setUndergroundTravelType', arguments: [nextTravelType] },
        );
      } else if (!nextTime && currentTravelType) {
        modifiers.push(
          { action: 'setUndergroundTravelTime', arguments: [nextTime] },
          { action: 'setUndergroundTravelType', arguments: [null] },
        );
      } else {
        modifiers.push({ action: 'setUndergroundTravelTime', arguments: [nextTime] });
      }

      onChange.call(null, ...modifiers);
    },
    300,
    [isSuburbanListing, onChange, travelType],
  );

  const handleTravelTypeChange = useCallback(
    (type: EMetroTravelType) => {
      const modifiers: TModifier[] = [{ action: 'setUndergroundTravelType', arguments: [type] }];

      if (!travelTime && type) {
        modifiers.push({ action: 'setUndergroundTravelTime', arguments: [DEFAULT_TRAVEL_TIME] });
      }

      onChange.call(null, ...modifiers);
    },
    [onChange, travelTime],
  );

  return (
    <AdvancedFilterRow label={label}>
      <InlineFilterGroup>
        <InlineFilter label={prefix} postfix={postfix} dataTestId="undergroundTravelTime">
          <MaskedInput
            inline
            width={50}
            placeholder="10"
            min={1}
            max={300}
            mask={Number}
            value={travelTime}
            onComplete={handleTravelTimeChange}
          />
        </InlineFilter>

        <InlineFilter dataTestId="undergroundTravelType">
          <SingleSelectionAdvancedFilter value={travelType} options={options} onFilterChange={handleTravelTypeChange} />
        </InlineFilter>
      </InlineFilterGroup>
    </AdvancedFilterRow>
  );
};
